import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Slider,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import ProductAutocomplete from '../../../shared/components/ProductAutocomplete';
import { useLocation } from 'react-router-dom';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import html2canvas from 'html2canvas';
import qzService from '../../../shared/utils/qzService';

function BarcodeLabelPrint() {
  const theme = useTheme();
  const location = useLocation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [columns, setColumns] = useState(() => Number(localStorage.getItem('barcodeColumns')) || 3);
  const [labelWidth, setLabelWidth] = useState(() => Number(localStorage.getItem('barcodeLabelWidth')) || 50); // mm
  const [labelHeight, setLabelHeight] = useState(() => Number(localStorage.getItem('barcodeLabelHeight')) || 30); // mm
  const [labelMarginTop, setLabelMarginTop] = useState(() => localStorage.getItem('barcodeLabelMarginTop') !== null ? Number(localStorage.getItem('barcodeLabelMarginTop')) : 1);
  const [labelMarginBottom, setLabelMarginBottom] = useState(() => localStorage.getItem('barcodeLabelMarginBottom') !== null ? Number(localStorage.getItem('barcodeLabelMarginBottom')) : 1);
  const [labelMarginLeft, setLabelMarginLeft] = useState(() => localStorage.getItem('barcodeLabelMarginLeft') !== null ? Number(localStorage.getItem('barcodeLabelMarginLeft')) : 1);
  const [labelMarginRight, setLabelMarginRight] = useState(() => localStorage.getItem('barcodeLabelMarginRight') !== null ? Number(localStorage.getItem('barcodeLabelMarginRight')) : 1);
  const [fontSize, setFontSize] = useState(() => Number(localStorage.getItem('barcodeFontSize')) || 10);
  const [showName, setShowName] = useState(() => localStorage.getItem('barcodeShowName') !== 'false');
  const [showPrice, setShowPrice] = useState(() => localStorage.getItem('barcodeShowPrice') !== 'false');
  const [showSku, setShowSku] = useState(() => localStorage.getItem('barcodeShowSku') === 'true');
  const [showBrand, setShowBrand] = useState(() => localStorage.getItem('barcodeShowBrand') === 'true');
  const [showCostCode, setShowCostCode] = useState(() => localStorage.getItem('barcodeShowCostCode') === 'true');
  const [showCompanyName, setShowCompanyName] = useState(true);
  const [isQzPrinting, setIsQzPrinting] = useState(false);

  useEffect(() => {
    localStorage.setItem('barcodeColumns', columns);
    localStorage.setItem('barcodeLabelWidth', labelWidth);
    localStorage.setItem('barcodeLabelHeight', labelHeight);
    localStorage.setItem('barcodeLabelMarginTop', labelMarginTop);
    localStorage.setItem('barcodeLabelMarginBottom', labelMarginBottom);
    localStorage.setItem('barcodeLabelMarginLeft', labelMarginLeft);
    localStorage.setItem('barcodeLabelMarginRight', labelMarginRight);
    localStorage.setItem('barcodeFontSize', fontSize);
    localStorage.setItem('barcodeShowName', showName);
    localStorage.setItem('barcodeShowPrice', showPrice);
    localStorage.setItem('barcodeShowSku', showSku);
    localStorage.setItem('barcodeShowBrand', showBrand);
    localStorage.setItem('barcodeShowCostCode', showCostCode);
  }, [columns, labelWidth, labelHeight, labelMarginTop, labelMarginBottom, labelMarginLeft, labelMarginRight, fontSize, showName, showPrice, showSku, showBrand, showCostCode]);

  const getCostCode = (costPrice) => {
    if (!company?.costPriceCodeMapping || company.costPriceCodeMapping.length < 10) return null;
    if (costPrice == null) return null;

    const priceStr = String(Math.round(costPrice));
    let code = '';
    const mapping = company.costPriceCodeMapping.toUpperCase();
    for (let char of priceStr) {
      if (char >= '0' && char <= '9') {
        code += mapping[parseInt(char, 10)];
      }
    }
    return code;
  };

  useEffect(() => {
    if (company) {
      setShowCompanyName(company.barcodePrintShowCompanyName !== false);
    }
  }, [company]);

  const handleAddProduct = (product) => {
    if (!product) return;
    const existing = selectedProducts.find((p) => p.id === product.id);
    if (existing) {
      setSelectedProducts(
        selectedProducts.map((p) =>
          p.id === product.id ? { ...p, qty: p.qty + 1 } : p
        )
      );
    } else {
      setSelectedProducts([...selectedProducts, { ...product, qty: 1 }]);
    }
  };

  useEffect(() => {
    if (location.state?.product) {
      handleAddProduct(location.state.product);
    }
  }, [location.state?.product]);

  const handleRemoveProduct = (id) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== id));
  };

  const handleQtyChange = (id, val) => {
    setSelectedProducts(
      selectedProducts.map((p) =>
        p.id === id ? { ...p, qty: Math.max(1, parseInt(val) || 1) } : p
      )
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleQzPrint = async () => {
    const qzPrinter = company?.qzBarcodePrinterName || company?.qzPrinterName;
    if (!qzPrinter) {
      alert('No QZ Printer Name configured for barcodes. Please select a printer in Settings.');
      return;
    }
    
    if (labels.length === 0) return;

    setIsQzPrinting(true);
    try {
      // Temporarily hide elements that shouldn't be printed and remove some constraints
      const printContainer = document.querySelector('.print-only');
      if (!printContainer) throw new Error('Print container not found');

      // Add a special class during capture to ensure proper rendering for QZ
      printContainer.classList.add('qz-capturing');
      
      const canvas = await html2canvas(printContainer, {
        scale: 2, // High resolution
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      printContainer.classList.remove('qz-capturing');

      const dataUrl = canvas.toDataURL('image/png');
      const base64Data = dataUrl.split(',')[1];

      await qzService.printImage(qzPrinter, base64Data, {
        jobName: `Barcodes-${Date.now()}`,
      });
      
    } catch (err) {
      console.error(err);
      alert(`QZ Print failed: ${err.message}`);
    } finally {
      setIsQzPrinting(false);
    }
  };

  // Generate labels array based on quantities
  const labels = [];
  selectedProducts.forEach((p) => {
    for (let i = 0; i < p.qty; i++) {
      labels.push(p);
    }
  });

  const isSingleLabelPerPage = Number(columns) === 1;
  const barcodeHeight = Math.max(5, Math.round(labelHeight * 0.25));
  const barcodeScale = Math.max(2, Math.min(6, Math.floor(labelWidth / 8)));

  return (
    <Box sx={{ pb: 5 }}>
      {/* Configuration Header - Hidden on Print */}
      <Box className="no-print">
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Barcode Label Printing
        </Typography>

        <Grid container spacing={3}>
          {/* Product Selection */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Select Products
              </Typography>
              <Box sx={{ mb: 3 }}>
                <ProductAutocomplete
                  value={null}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      handleAddProduct(newValue);
                    }
                  }}
                  placeholder="Search by name, SKU or barcode..."
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {selectedProducts.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', opacity: 0.5 }}>
                  <AddIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography>No products selected</Typography>
                </Box>
              ) : (
                <Box>
                  {selectedProducts.map((p) => (
                    <Box
                      key={p.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          SKU: {p.sku} | Barcode: {p.barcode || 'N/A'}
                        </Typography>
                      </Box>
                      <TextField
                        size="small"
                        type="number"
                        label="Qty"
                        value={p.qty}
                        onChange={(e) => handleQtyChange(p.id, e.target.value)}
                        sx={{ width: 80 }}
                      />
                      <IconButton color="error" onClick={() => handleRemoveProduct(p.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Configuration */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Label Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Columns"
                    size="small"
                    value={columns}
                    onChange={(e) => setColumns(e.target.value)}
                  >
                    {[1, 2, 3, 4, 5].map((c) => (
                      <MenuItem key={c} value={c}>{c} Column{c > 1 ? 's' : ''}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Font Size (px)"
                    size="small"
                    type="number"
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Width (mm)"
                    size="small"
                    type="number"
                    value={labelWidth}
                    onChange={(e) => setLabelWidth(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Height (mm)"
                    size="small"
                    type="number"
                    value={labelHeight}
                    onChange={(e) => setLabelHeight(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Top Margin (mm)"
                    size="small"
                    type="number"
                    inputProps={{ step: 0.1 }}
                    value={labelMarginTop}
                    onChange={(e) => setLabelMarginTop(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Bottom Margin (mm)"
                    size="small"
                    type="number"
                    inputProps={{ step: 0.1 }}
                    value={labelMarginBottom}
                    onChange={(e) => setLabelMarginBottom(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Left Margin (mm)"
                    size="small"
                    type="number"
                    inputProps={{ step: 0.1 }}
                    value={labelMarginLeft}
                    onChange={(e) => setLabelMarginLeft(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Right Margin (mm)"
                    size="small"
                    type="number"
                    inputProps={{ step: 0.1 }}
                    value={labelMarginRight}
                    onChange={(e) => setLabelMarginRight(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  control={<Checkbox checked={showName} onChange={(e) => setShowName(e.target.checked)} />}
                  label="Show Product Name"
                />
                <FormControlLabel
                  control={<Checkbox checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />}
                  label="Show Price"
                />
                <FormControlLabel
                  control={<Checkbox checked={showSku} onChange={(e) => setShowSku(e.target.checked)} />}
                  label="Show SKU"
                />
                <FormControlLabel
                  control={<Checkbox checked={showBrand} onChange={(e) => setShowBrand(e.target.checked)} />}
                  label="Show Brand"
                />
                <FormControlLabel
                  control={<Checkbox checked={showCostCode} onChange={(e) => setShowCostCode(e.target.checked)} />}
                  label="Show Cost Code"
                />
                <FormControlLabel
                  control={<Checkbox checked={showCompanyName} onChange={(e) => setShowCompanyName(e.target.checked)} />}
                  label="Show Company Name"
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={selectedProducts.length === 0}
                sx={{ mt: 3, py: 1.5 }}
              >
                Print Labels (Browser)
              </Button>
              
              {company?.enableQzPrint && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<PrintIcon />}
                  onClick={handleQzPrint}
                  disabled={selectedProducts.length === 0 || isQzPrinting}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {isQzPrinting ? 'Printing...' : `Direct Print (QZ)`}
                </Button>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Print Preview Area */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center'
        }}
        className="no-print"
      >
        <Typography variant="body2" color="text.secondary">
          Preview Mode - Use "Print Labels" to see the final layout
        </Typography>
      </Box>

      {/* Actual Print Content */}
      <div className="print-only">
        <style dangerouslySetInnerHTML={{
          __html: `
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background: white;
              overflow: hidden !important;
            }
            body * { visibility: hidden; }
            .print-only, .print-only * { visibility: visible; }
            .print-only { 
              position: absolute !important; 
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .no-print { display: none !important; }
            @page {
              size: ${isSingleLabelPerPage ? `${labelWidth}mm ${labelHeight}mm` : 'auto'} !important;
              margin: ${isSingleLabelPerPage ? '0' : '5mm'} !important;
            }
            .barcode-label {
              width: ${labelWidth}mm !important;
              height: ${labelHeight}mm !important;
              border: none !important;
              box-shadow: none !important;
              background: none !important;
              padding: ${labelMarginTop}mm ${labelMarginRight}mm ${labelMarginBottom}mm ${labelMarginLeft}mm !important;
              margin: 0 !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-sizing: border-box !important;
            }
            ${isSingleLabelPerPage ? `
              .barcode-label { page-break-after: auto !important; break-after: auto !important; }
              .barcode-label:not(:last-child) { page-break-after: always !important; break-after: page !important; }
            ` : `
              /* Sheet mode (2+ columns): never force page breaks per label */
              .barcode-label { page-break-after: auto !important; break-after: auto !important; }
            `}
          }
          
          /* Styles when capturing for QZ */
          .print-only.qz-capturing {
            position: relative !important;
            visibility: visible !important;
            display: block !important;
            width: fit-content !important;
          }
          .qz-capturing .label-grid {
             display: grid !important;
             grid-template-columns: ${columns > 1 ? `repeat(${columns}, 1fr)` : '1fr'} !important;
          }

          .label-grid {
            display: ${isSingleLabelPerPage ? 'block' : 'grid'};
            ${isSingleLabelPerPage ? '' : `grid-template-columns: repeat(${columns}, 1fr);`}
            gap: ${columns > 1 ? '2mm' : '0'};
          }
          .barcode-label {
            width: ${labelWidth}mm;
            height: ${labelHeight}mm;
            border: 1px dashed #ccc;
            padding: ${labelMarginTop}mm ${labelMarginRight}mm ${labelMarginBottom}mm ${labelMarginLeft}mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            gap: 0.35mm;
            overflow: hidden;
            text-align: center;
            background: white;
            color: black;
            box-sizing: border-box;
          }
          .barcode-label .company-name {
            font-size: ${fontSize - 2}px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.2px;
            color: #222;
            margin: 0;
            line-height: 1.1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }
          .barcode-label .product-name {
            font-size: ${fontSize}px;
            font-weight: 600;
            color: #000;
            line-height: 1.1;
            margin: 0.2mm 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }
          .barcode-label .sku {
            font-size: ${fontSize - 3}px;
            color: #666;
            margin: 0;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
          }
          .barcode-label .barcode-container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: ${barcodeHeight}mm;
            margin-top: 0.4mm;
          }
          .barcode-label .barcode-img {
            width: 100%;
            height: ${barcodeHeight}mm;
            object-fit: contain;
            image-rendering: pixelated;
          }
          .barcode-label .barcode-text {
            font-size: ${Math.max(10, fontSize)}px;
            font-weight: 800;
            color: #111;
            line-height: 1;
            margin: 0;
            letter-spacing: 0.3px;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
          }
          .barcode-label .price {
            font-size: ${fontSize + 1}px;
            font-weight: 800;
            color: #000;
            line-height: 1.1;
            margin: 0;
            letter-spacing: -0.2px;
          }
        `}} />

        <div className="label-grid">
          {labels.map((p, idx) => (
            <div key={`${p.id}-${idx}`} className="barcode-label">
              {showCompanyName && <div className="company-name">{company?.name || 'Company Name'}</div>}
              {(showName || showBrand) && (
                <div className="product-name">
                  {showBrand && p.brand ? `${p.brand} ` : ''}{showName ? p.name : ''}
                </div>
              )}
              {showSku && <div className="sku">SKU: {p.sku}</div>}
              {p.barcode && (
                <div className="barcode-container">
                  <img
                    className="barcode-img"
                    src={`https://bwipjs-api.metafloor.com/?bcid=code128&text=${encodeURIComponent(p.barcode)}&includetext=false&scale=${barcodeScale}&height=${barcodeHeight}&format=png&paddingwidth=0&paddingheight=0&ts=${idx}`}
                    alt="barcode"
                  />
                </div>
              )}
              {!p.barcode && (
                <div style={{ fontSize: '7px', color: 'red', margin: '1mm 0' }}>[No Barcode]</div>
              )}

              {p.barcode && <div className="barcode-text">{p.barcode}</div>}

              {showCostCode && getCostCode(p.costPrice) && (
                <div style={{ fontSize: `${fontSize - 1}px`, fontWeight: 600, color: '#333', marginTop: '1px', lineHeight: 1 }}>
                  {getCostCode(p.costPrice)}Z
                </div>
              )}

              {showPrice && (
                <div className="price">
                  {currency} {Number(p.sellingPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
}

export default BarcodeLabelPrint;
